import { useCallback, useEffect, useState } from 'react';
import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn as getMeLoggedIn } from '@api/selectors/auth';
import { getIsLoggedIn } from '@selectors/auth';
import { readCookie } from '@utils/cookies';
import { CampaignInvalidScreenError, fetchCampaignState } from './api';
import CampaignLoginDrawer from './CampaignLoginDrawer';
import { REFERRAL_PROMOTION_VIEWED, promotionPartForScreen } from './segment';
import PollScreen from './PollScreen';
import SquadScreen from './SquadScreen';
import RevealScreen from './RevealScreen';
import type { CampaignState, ReferralSquadData } from './types';
import { PageShell } from './shared';

const globalStyles = css`
  html,
  body,
  #__next {
    background: #000;
    min-height: 100%;
  }
`;

const LoadingShell = styled(PageShell)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
`;

const CampaignLoading = () => (
  <>
    <Global styles={globalStyles} />
    <LoadingShell>Loading...</LoadingShell>
  </>
);

const ReferralPromotions = () => {
  const router = useRouter();
  const { pathname, isReady, query } = router;
  const isLoggedIn = useSelector(getIsLoggedIn);
  const meLoggedIn = useMyInfo(getMeLoggedIn);
  const { isValidating } = useMyInfo();
  const hasToken = Boolean(readCookie('accessToken'));
  const authed = isLoggedIn || Boolean(meLoggedIn);
  const waitingForAuth = !authed && hasToken && isValidating;
  const [state, setState] = useState<CampaignState | null>(null);
  const [error, setError] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const leaveRoute = useCallback(() => {
    window.location.assign('/');
  }, []);

  useEffect(() => {
    if (!isReady || query.screen === undefined) return;
    router.replace(pathname, pathname, { shallow: true });
  }, [isReady, pathname, query.screen, router]);

  useEffect(() => {
    if (!authed) {
      setState(null);
      return undefined;
    }

    let cancelled = false;
    setError(false);

    fetchCampaignState()
      .then((response) => {
        if (!cancelled) setState(response);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof CampaignInvalidScreenError) {
          router.replace('/404');
          return;
        }
        setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [authed, router]);

  useEffect(() => {
    if (!state?.screen) return;
    segment.track(REFERRAL_PROMOTION_VIEWED, {
      promotion_part: promotionPartForScreen(state.screen)
    });
  }, [state?.screen]);

  const showCampaignLoading =
    !hasMounted || ((authed || waitingForAuth) && !error && state === null);

  if (showCampaignLoading) {
    return <CampaignLoading />;
  }

  if (!authed && !waitingForAuth) {
    return (
      <>
        <Global styles={globalStyles} />
        <CampaignLoginDrawer
          open
          onClose={leaveRoute}
          redirectPath={pathname}
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Global styles={globalStyles} />
        <LoadingShell>Unable to load</LoadingShell>
      </>
    );
  }

  const handleSquadUpdate = (squadData: ReferralSquadData) => {
    setState((current) =>
      current
        ? {
            ...current,
            squad: {
              ...current.squad,
              ...squadData
            }
          }
        : current
    );
  };

  return (
    <>
      <Global styles={globalStyles} />
      {state.screen === 'squad' && (
        <SquadScreen data={state.squad} onSquadUpdate={handleSquadUpdate} />
      )}
      {state.screen === 'reveal' && (
        <RevealScreen
          data={state.reveal}
          squad={state.squad}
          onSquadUpdate={handleSquadUpdate}
        />
      )}
      {state.screen === 'poll' && <PollScreen data={state.poll} />}
    </>
  );
};

export default ReferralPromotions;
